import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Wallet, Plus, ArrowDownLeft, ArrowUpRight, TrendingUp,
  RefreshCw, CreditCard, AlertTriangle, CheckCircle, XCircle,
  Landmark, Send, Clock, Edit, DollarSign, ShieldCheck,
} from 'lucide-react';
import { initMercadoPago, Payment } from '@mercadopago/sdk-react';
import { walletAPI } from '../../services/api';
import { formatPrice } from '../../utils/helpers';
import { toast } from '../../hooks/use-toast';

const TYPE_MAP = {
  recharge: { icon: ArrowDownLeft, label: 'Recarga', color: 'text-green-600' },
  ad_spend: { icon: ArrowUpRight, label: 'Gasto Ads', color: 'text-red-500' },
  order_wallet_payment: { icon: ArrowUpRight, label: 'Pago Pedido', color: 'text-red-500' },
  order_card_payment: { icon: CreditCard, label: 'Pago Tarjeta', color: 'text-blue-500' },
  subscription: { icon: TrendingUp, label: 'Suscripcion', color: 'text-blue-500' },
  seller_earning: { icon: ArrowDownLeft, label: 'Venta', color: 'text-emerald-600' },
  delivery_earning: { icon: ArrowDownLeft, label: 'Entrega', color: 'text-cyan-600' },
  withdrawal: { icon: Send, label: 'Retiro', color: 'text-orange-500' },
  manual_adjustment: { icon: DollarSign, label: 'Ajuste', color: 'text-purple-500' },
};

const STATUS_MAP = {
  completed: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50' },
  failed: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50' },
  pending: { icon: Clock, color: 'text-amber-500', bg: 'bg-amber-50' },
  rejected: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50' },
};

let mpInitialized = false;

const UserWallet = () => {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  // Recharge
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [mpActive, setMpActive] = useState(false);
  const [brickReady, setBrickReady] = useState(false);
  // Bank
  const [bankEdit, setBankEdit] = useState(false);
  const [bankForm, setBankForm] = useState({ clabe_account: '', bank_name: '', account_holder: '' });
  const [savingBank, setSavingBank] = useState(false);
  // Withdrawal
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await walletAPI.getWallet();
      setWallet(data);
      setBankForm({
        clabe_account: data.clabe_account || '',
        bank_name: data.bank_name || '',
        account_holder: data.account_holder || '',
      });
    } catch {
      toast({ title: 'Error cargando billetera', variant: 'destructive' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Recharge Logic ──
  const openRecharge = async () => {
    try {
      const { public_key, active } = await walletAPI.getMPPublicKey();
      setMpActive(active);
      setRechargeAmount('');
      setBrickReady(false);
      if (public_key && active) {
        if (!mpInitialized) {
          initMercadoPago(public_key, { locale: 'es-MX' });
          mpInitialized = true;
        }
        setBrickReady(true);
      }
      setRechargeOpen(true);
    } catch {
      toast({ title: 'Error conectando pasarela', variant: 'destructive' });
    }
  };

  const handleBrickSubmit = async (formData) => {
    const amt = parseFloat(rechargeAmount);
    if (!amt || amt <= 0) {
      toast({ title: 'Selecciona un monto primero', variant: 'destructive' });
      return;
    }
    try {
      const result = await walletAPI.recharge({
        token: formData.token, amount: amt,
        payment_method_id: formData.payment_method_id,
        installments: Number(formData.installments) || 1,
        issuer_id: formData.issuer_id || null,
        payer_email: formData.payer?.email || null,
      });
      toast({ title: `Recarga exitosa! Nuevo saldo: ${formatPrice(result.new_balance)}` });
      setRechargeOpen(false);
      load();
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Error en el pago', variant: 'destructive' });
    }
  };

  // ── Bank Account Logic ──
  const handleSaveBank = async () => {
    setSavingBank(true);
    try {
      await walletAPI.updateBankAccount(bankForm);
      toast({ title: 'Datos bancarios guardados' });
      setBankEdit(false);
      load();
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Error guardando datos', variant: 'destructive' });
    } finally { setSavingBank(false); }
  };

  // ── Withdrawal Logic ──
  const handleWithdraw = async () => {
    const amt = parseFloat(withdrawAmount);
    if (!amt || amt <= 0) return;
    setWithdrawing(true);
    try {
      const res = await walletAPI.withdraw(amt);
      toast({ title: res.message || 'Solicitud creada' });
      setWithdrawOpen(false);
      setWithdrawAmount('');
      load();
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setWithdrawing(false); }
  };

  const balance = wallet?.balance || 0;
  const pendingBalance = wallet?.pending_balance || 0;
  const hasClabe = !!wallet?.clabe_account;
  const txns = wallet?.transactions || [];

  return (
    <div className="space-y-6" data-testid="user-wallet">
      {/* ═══ BALANCE PANEL ═══ */}
      <Card className="overflow-hidden" data-testid="wallet-balance-card">
        <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-6 text-white">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-medium text-white/80">Saldo Disponible</p>
            <button onClick={load} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors" data-testid="refresh-wallet-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <p className="text-4xl font-black tracking-tight" data-testid="wallet-balance">
            {loading ? '...' : formatPrice(balance)}
          </p>
          {pendingBalance > 0 && (
            <div className="flex items-center gap-2 mt-3 bg-white/15 rounded-lg px-3 py-2" data-testid="pending-balance">
              <Clock className="w-4 h-4 text-white/70" />
              <div>
                <p className="text-xs text-white/70">Saldo en Transito (proximo a liberarse)</p>
                <p className="text-lg font-bold">{formatPrice(pendingBalance)}</p>
              </div>
            </div>
          )}
        </div>
        <div className="p-4 flex gap-3 bg-gray-50">
          <Button onClick={openRecharge} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white" data-testid="recharge-btn">
            <Plus className="w-4 h-4 mr-2" /> Recargar
          </Button>
          <Button onClick={() => { if (!hasClabe) { toast({ title: 'Configura tu cuenta CLABE primero', variant: 'destructive' }); return; } setWithdrawAmount(''); setWithdrawOpen(true); }}
            variant="outline" className="flex-1 border-orange-300 text-orange-600 hover:bg-orange-50" disabled={!hasClabe}
            data-testid="withdraw-btn">
            <Send className="w-4 h-4 mr-2" /> Solicitar Retiro
          </Button>
        </div>
      </Card>

      {/* ═══ BANK ACCOUNT SECTION ═══ */}
      <Card className="p-5 border" data-testid="bank-account-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2 text-gray-800">
            <Landmark className="w-4 h-4 text-gray-500" /> Cuenta Bancaria
          </h3>
          {!bankEdit && (
            <Button variant="ghost" size="sm" onClick={() => setBankEdit(true)} className="text-orange-500 hover:text-orange-600" data-testid="edit-bank-btn">
              <Edit className="w-4 h-4 mr-1" /> {hasClabe ? 'Editar' : 'Configurar'}
            </Button>
          )}
        </div>

        {bankEdit ? (
          <div className="space-y-3" data-testid="bank-form">
            <div>
              <Label className="text-sm text-gray-600">Banco</Label>
              <Input value={bankForm.bank_name} onChange={e => setBankForm(p => ({ ...p, bank_name: e.target.value }))}
                placeholder="Ej: BBVA, Banorte, HSBC..." className="mt-1" data-testid="bank-name-input" />
            </div>
            <div>
              <Label className="text-sm text-gray-600">Titular de la cuenta</Label>
              <Input value={bankForm.account_holder} onChange={e => setBankForm(p => ({ ...p, account_holder: e.target.value }))}
                placeholder="Nombre completo del titular" className="mt-1" data-testid="account-holder-input" />
            </div>
            <div>
              <Label className="text-sm text-gray-600">CLABE Interbancaria (18 digitos)</Label>
              <Input value={bankForm.clabe_account} onChange={e => setBankForm(p => ({ ...p, clabe_account: e.target.value.replace(/\D/g, '').slice(0, 18) }))}
                placeholder="000000000000000000" maxLength={18}
                className="mt-1 font-mono tracking-wider" data-testid="clabe-input" />
              <p className="text-xs text-gray-400 mt-1">{bankForm.clabe_account.length}/18 digitos</p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleSaveBank} disabled={savingBank || bankForm.clabe_account.length !== 18}
                className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-bank-btn">
                {savingBank ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4 mr-2" /> Guardar</>}
              </Button>
              <Button variant="outline" onClick={() => setBankEdit(false)}>Cancelar</Button>
            </div>
          </div>
        ) : hasClabe ? (
          <div className="bg-gray-50 rounded-lg p-4 space-y-2" data-testid="bank-info">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Banco</span>
              <span className="font-medium">{wallet?.bank_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Titular</span>
              <span className="font-medium">{wallet?.account_holder}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">CLABE</span>
              <span className="font-mono font-medium">{wallet?.clabe_account?.replace(/(\d{4})/g, '$1 ').trim()}</span>
            </div>
          </div>
        ) : (
          <div className="text-center py-6 text-gray-400" data-testid="no-bank-info">
            <Landmark className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No tienes cuenta bancaria configurada</p>
            <p className="text-xs mt-1">Configurala para solicitar retiros</p>
          </div>
        )}
      </Card>

      {/* ═══ TRANSACTIONS TABLE ═══ */}
      <Card className="border" data-testid="transactions-table">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <Wallet className="w-4 h-4 text-orange-500" /> Historial de transacciones
          </h3>
          <span className="text-xs text-gray-400">{txns.length} registros</span>
        </div>

        {txns.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Wallet className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>Sin transacciones aun</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-medium text-gray-500">Tipo</th>
                  <th className="text-right p-3 font-medium text-gray-500">Monto</th>
                  <th className="text-center p-3 font-medium text-gray-500">Estado</th>
                  <th className="text-left p-3 font-medium text-gray-500">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t, i) => {
                  const tm = TYPE_MAP[t.type] || TYPE_MAP.recharge;
                  const sm = STATUS_MAP[t.status] || STATUS_MAP.pending;
                  const Icon = tm.icon;
                  const SIcon = sm.icon;
                  return (
                    <tr key={i} className="border-b hover:bg-gray-50" data-testid={`txn-row-${i}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${tm.color}`} />
                          <span className="text-gray-700">{tm.label}</span>
                        </div>
                      </td>
                      <td className={`p-3 text-right font-mono font-bold ${t.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {t.amount >= 0 ? '+' : ''}{formatPrice(Math.abs(t.amount))}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${sm.bg} ${sm.color}`}>
                          <SIcon className="w-3 h-3" /> {t.status}
                        </span>
                      </td>
                      <td className="p-3 text-gray-500 text-xs">
                        {t.created_at ? new Date(t.created_at).toLocaleString('es-MX') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ═══ RECHARGE MODAL ═══ */}
      <Dialog open={rechargeOpen} onOpenChange={setRechargeOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="recharge-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-orange-500" /> Recargar saldo
            </DialogTitle>
          </DialogHeader>
          {!mpActive ? (
            <div className="text-center py-8">
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">Pasarela de pago no disponible</p>
              <p className="text-gray-400 text-sm mt-1">El administrador debe configurar MercadoPago</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Monto a recargar (MXN)</label>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {[100, 250, 500, 1000].map(v => (
                    <button key={v} type="button" onClick={() => setRechargeAmount(String(v))}
                      className={`py-2 rounded-lg text-sm font-bold border transition-all ${
                        rechargeAmount === String(v) ? 'bg-orange-500 text-white border-orange-500' : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-orange-300'
                      }`} data-testid={`amount-${v}`}>
                      ${v}
                    </button>
                  ))}
                </div>
                <Input type="number" min="10" step="10" value={rechargeAmount} onChange={e => setRechargeAmount(e.target.value)}
                  placeholder="Otro monto..." className="text-center font-mono text-lg" data-testid="recharge-amount-input" />
              </div>
              {brickReady && rechargeAmount && parseFloat(rechargeAmount) > 0 && (
                <div data-testid="mp-payment-brick">
                  <Payment
                    initialization={{ amount: parseFloat(rechargeAmount) }}
                    customization={{ paymentMethods: { creditCard: 'all', debitCard: 'all' }, visual: { style: { theme: 'default' } } }}
                    onSubmit={async ({ formData }) => { await handleBrickSubmit(formData); }}
                    onError={(error) => { console.error('MP Brick error:', error); toast({ title: 'Error en el formulario de pago', variant: 'destructive' }); }}
                  />
                </div>
              )}
              {(!rechargeAmount || parseFloat(rechargeAmount) <= 0) && (
                <p className="text-center text-sm text-gray-400 py-4">Selecciona o ingresa un monto para mostrar el formulario de pago</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ═══ WITHDRAWAL MODAL ═══ */}
      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent className="max-w-md" data-testid="withdraw-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-orange-500" /> Solicitar Retiro
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Saldo disponible</p>
              <p className="text-2xl font-bold text-gray-900">{formatPrice(balance)}</p>
              <div className="mt-2 text-xs text-gray-500 space-y-1">
                <p>Banco: <strong>{wallet?.bank_name}</strong></p>
                <p>CLABE: <strong className="font-mono">{wallet?.clabe_account}</strong></p>
              </div>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Monto a retirar (MXN)</Label>
              <Input type="number" min="1" max={balance} step="0.01"
                value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)}
                placeholder="0.00" className="text-center font-mono text-lg mt-1" data-testid="withdraw-amount-input" />
              {withdrawAmount && parseFloat(withdrawAmount) > balance && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Excede tu saldo disponible
                </p>
              )}
            </div>
            <Button onClick={handleWithdraw}
              disabled={withdrawing || !withdrawAmount || parseFloat(withdrawAmount) <= 0 || parseFloat(withdrawAmount) > balance}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white" data-testid="confirm-withdraw-btn">
              {withdrawing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Solicitar Transferencia SPEI
            </Button>
            <p className="text-[10px] text-gray-400 text-center">
              El dinero sera transferido a tu cuenta CLABE. Procesamiento: 1-24 horas habiles.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserWallet;
